import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Filter, ShieldCheck, Globe, Sparkles, ExternalLink, Send } from "lucide-react";

const SERVICE_LABELS = {
  checkin: "Accueil voyageurs", key_handover: "Remise des clés", cleaning: "Ménage",
  linen: "Linge", photo: "Reportage photo", maintenance: "Maintenance",
  conciergerie_full: "Conciergerie clé en main", garden: "Jardin / piscine",
};

/**
 * FindConciergeriePanel — owners-only tab that pre-filters the partners directory
 * by the city/region of one of their listings.
 */
export default function FindConciergeriePanel() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState("");
  const [partnerType, setPartnerType] = useState("");
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/listings", { params: { owner_id: user.id } }).then(({ data }) => {
      setListings(data);
      if (data.length) setSelectedListingId(data[0].id);
    });
  }, [user.id]);

  const selectedListing = useMemo(() => listings.find((l) => l.id === selectedListingId), [listings, selectedListingId]);

  useEffect(() => {
    if (!selectedListing) { setPartners([]); return; }
    setLoading(true);
    const params = {};
    if (selectedListing.city) params.city = selectedListing.city;
    if (selectedListing.region) params.region = selectedListing.region;
    if (partnerType) params.partner_type = partnerType;
    api.get("/partners", { params }).then(({ data }) => setPartners(data)).finally(() => setLoading(false));
  }, [selectedListing, partnerType]);

  if (!listings.length) {
    return (
      <div className="max-w-4xl">
        <h2 className="font-display text-4xl font-bold tracking-tight mb-4">Trouver une conciergerie</h2>
        <div className="border-2 border-dashed border-black/15 p-12 text-center">
          <p className="text-neutral-600">Créez d'abord une annonce — nous vous proposerons ensuite les partenaires disponibles dans votre secteur.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Réseau MSB</div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-3">Trouver une conciergerie ou un partenaire ménage</h2>
      <p className="text-neutral-600 mb-8 max-w-2xl">
        Sélectionnez l'une de vos annonces : nous filtrons automatiquement notre annuaire de prestataires vérifiés selon votre secteur géographique.
      </p>

      {/* Filters */}
      <div className="bg-white border border-black/10 p-4 mb-6 flex flex-wrap gap-3 items-center" data-testid="conc-filters">
        <Filter className="w-4 h-4" />
        <select
          data-testid="conc-listing"
          value={selectedListingId}
          onChange={(e) => setSelectedListingId(e.target.value)}
          className="text-sm border border-black/15 px-3 py-2 bg-white max-w-xs"
        >
          {listings.map((l) => (
            <option key={l.id} value={l.id}>
              {l.title} — {l.city}, {l.region}
            </option>
          ))}
        </select>
        <select
          data-testid="conc-type"
          value={partnerType}
          onChange={(e) => setPartnerType(e.target.value)}
          className="text-sm border border-black/15 px-3 py-2 bg-white"
        >
          <option value="">Conciergerie + ménage</option>
          <option value="conciergerie">Conciergerie uniquement</option>
          <option value="menage">Ménage uniquement</option>
        </select>
        {selectedListing && (
          <div className="text-xs text-neutral-500 ml-auto flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            Secteur : <strong>{selectedListing.city}, {selectedListing.region}</strong>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-neutral-500 text-sm">Recherche…</div>
      ) : partners.length === 0 ? (
        <div className="border-2 border-dashed border-black/15 p-10 text-center">
          <Sparkles className="w-6 h-6 mx-auto mb-3 text-[#7A1535]" />
          <p className="font-semibold mb-2">Pas encore de partenaire dans ce secteur</p>
          <p className="text-sm text-neutral-600 mb-4">Nous développons activement notre réseau. En attendant, écrivez à notre équipe et nous trouverons une solution.</p>
          <Link to="/support" className="inline-block bg-[#0A0A0A] text-white px-5 py-2.5 text-sm font-semibold">Demander à l'équipe MSB</Link>
        </div>
      ) : (
        <>
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">
            {partners.length} partenaire{partners.length > 1 ? "s" : ""} dans votre secteur
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {partners.map((p) => (
              <div key={p.id} className="border border-black/10 bg-white p-5 flex flex-col" data-testid={`conc-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
                      {p.partner_type === "conciergerie" ? "Conciergerie" : "Ménage"}
                    </div>
                    <div className="font-display text-lg font-bold mt-0.5 leading-tight">{p.company_name}</div>
                  </div>
                  {p.insurance && (
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#166534] text-white px-2 py-1 flex items-center gap-1 shrink-0">
                      <ShieldCheck className="w-3 h-3" /> Assuré
                    </span>
                  )}
                </div>
                <div className="text-xs text-neutral-600 flex items-center gap-1.5 mb-2">
                  <MapPin className="w-3 h-3" /> {[...(p.regions || []), ...(p.cities || [])].slice(0, 3).join(" · ")}
                </div>
                <p className="text-sm text-neutral-700 line-clamp-2 mb-3 flex-1">{p.description}</p>
                {p.services?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {p.services.slice(0, 4).map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-[0.1em] font-bold px-2 py-0.5 bg-[#F5F2EC] border border-black/10">
                        {SERVICE_LABELS[s] || s}
                      </span>
                    ))}
                  </div>
                )}
                {p.price_range && <div className="text-xs text-neutral-700 mb-3"><strong>Tarif :</strong> {p.price_range}</div>}
                <div className="border-t border-black/10 pt-3 flex items-center gap-3 mt-auto">
                  <Link to={`/partenaires/${p.id}`} className="text-sm font-bold underline underline-offset-4 flex items-center gap-1">
                    Voir contact <ExternalLink className="w-3 h-3" />
                  </Link>
                  {p.website && (
                    <a href={p.website} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-neutral-500 hover:text-[#0A0A0A] flex items-center gap-1">
                      <Globe className="w-3 h-3" /> Site
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-8 border-t border-black/10 pt-6 flex items-start gap-3 text-sm text-neutral-600">
        <Send className="w-4 h-4 mt-0.5 shrink-0" />
        <p>
          Vous connaissez un prestataire de confiance qui n'est pas encore référencé ?
          <Link to="/devenir-partenaire" className="ml-1 font-bold underline">Invitez-le à rejoindre le réseau MSB</Link>.
        </p>
      </div>
    </div>
  );
}
